// ApplicationList.tsx
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';
import { getStorage, ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import { processResume, matchResumeToApplication, analyzeJobDescription } from '../services/resumeMatchingFunctions';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';

import type { JobApplication, ApplicationStatus } from '@shared/types';
import { STATUS_LABELS, STATUS_COLORS } from '@shared/constants';
import AddApplicationModal from './AddApplicationModal';
import ApplicationDetailModal from './ApplicationDetailModal';
import { Timestamp } from 'firebase/firestore';

const COLUMNS: ApplicationStatus[] = ['applied', 'interview', 'offer', 'rejected'];

interface ApplicationListProps {
  userId: string;
  selectedDate?: string | null;
  onClearDateFilter?: () => void;
}

export default function ApplicationList({ userId, selectedDate, onClearDateFilter }: ApplicationListProps) {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [archiveModal, setArchiveModal] = useState<{ open: boolean; appId?: string }>({ open: false });
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const selectedApp = useMemo(() => selectedAppId ? applications.find(a => a.id === selectedAppId) || null : null, [selectedAppId, applications]);

  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Load applications
  useEffect(() => { loadApplications(); }, [userId]);

  // Handle deep linking from extension
  useEffect(() => {
    const appId = searchParams.get('appId');
    const mode = searchParams.get('mode');
    if (appId && applications.length > 0) {
      const exists = applications.find(a => a.id === appId);
      if (exists) {
        if (mode === 'edit-resume') {
          navigate(`/applications/${appId}?mode=edit`);
        } else {
          setSelectedAppId(appId);
        }
      }
      
      // Clear params once we've processed them (or if they are invalid)
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('appId');
      newParams.delete('mode');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, applications.length]);
  const loadApplications = async () => {
    setLoading(true);
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'applications'),
        where('userId', '==', userId),
        orderBy('appliedDate', 'desc')
      );
      const snapshot = await getDocs(q);
      const apps: JobApplication[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as JobApplication));
      setApplications(apps);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // --- Status change ---
  const handleStatusChange = async (appId: string, newStatus: ApplicationStatus) => {
    const db = getFirestore();
    const refDoc = doc(db, 'applications', appId);
    try {
      await updateDoc(refDoc, {
        status: newStatus,
        updatedAt: serverTimestamp(),
        statusHistory: arrayUnion({ status: newStatus, timestamp: Timestamp.now() })
      });
      setApplications(apps => apps.map(app => (app.id === appId ? { ...app, status: newStatus } : app)));
    } catch (e) { console.error('Failed to update status', e); }
  };

  // --- Archive ---
  const handleArchive = async () => {
    if (!archiveModal.appId) return;
    const appId = archiveModal.appId;
    const db = getFirestore();
    const refDoc = doc(db, 'applications', appId);
    try {
      await updateDoc(refDoc, { archived: true, updatedAt: serverTimestamp() });
      setApplications(apps => apps.map(app => (app.id === appId ? { ...app, archived: true } : app)));
      setArchiveModal({ open: false });
    } catch (e) { console.error('Failed to archive', e); }
  };

  // --- Download ---
  const handleDownload = async (path?: string) => {
    if (!path) return;
    const storage = getStorage();
    const url = await getDownloadURL(ref(storage, path));
    window.open(url, '_blank');
  };

  // --- Upload Resume/Cover Letter ---
  const handleFileUpload = async (appId: string, file: File, fileType: 'resume' | 'coverLetter') => {
    try {
      const storage = getStorage();
      const db = getFirestore();
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const folder = fileType === 'resume' ? 'resumes' : 'cover-letters';
      const storagePath = `users/${userId}/${folder}/${timestamp}_${safeName}`;

      // Upload to Firebase Storage
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      // Update Firestore document
      const refDoc = doc(db, 'applications', appId);
      const updateData = fileType === 'resume'
        ? { resumeUrl: downloadUrl, resumePath: storagePath, updatedAt: serverTimestamp() }
        : { coverLetterUrl: downloadUrl, coverLetterPath: storagePath, updatedAt: serverTimestamp() };

      await updateDoc(refDoc, updateData);

      // Update local state
      setApplications(apps => apps.map(app => {
        if (app.id === appId) {
          return fileType === 'resume'
            ? { ...app, resumeUrl: downloadUrl, resumePath: storagePath }
            : { ...app, coverLetterUrl: downloadUrl, coverLetterPath: storagePath };
        }
        return app;
      }));

      // For resumes: create SavedResume and link to application (no auto-processing)
      if (fileType === 'resume') {
        try {
          const appData = applications.find(a => a.id === appId);
          const resumeName = appData ? `${appData.company} - ${appData.jobTitle}` : file.name.replace(/\.[^/.]+$/, '');

          // Create SavedResume doc
          const savedResumeRef = await addDoc(collection(db, 'savedResumes'), {
            userId,
            name: resumeName,
            fileName: file.name,
            storagePath,
            downloadUrl,
            size: file.size,
            skills: [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });

          // Link resume to application
          await updateDoc(refDoc, { matchedResumeId: savedResumeRef.id, updatedAt: serverTimestamp() });
          setApplications(apps => apps.map(app =>
            app.id === appId ? { ...app, matchedResumeId: savedResumeRef.id } : app
          ));
        } catch (analysisErr) {
          console.warn('Resume uploaded but SavedResume creation failed:', analysisErr);
        }
      }
    } catch (e) {
      console.error('Failed to upload file', e);
      alert('Failed to upload file. Please try again.');
    }
  };

  // --- Save JD from modal ---
  const handleSaveJd = async (appId: string, description: string) => {
    const db = getFirestore();
    const refDoc = doc(db, 'applications', appId);
    await updateDoc(refDoc, { description, updatedAt: serverTimestamp() });
    setApplications(apps => apps.map(app =>
      app.id === appId ? { ...app, description } : app
    ));
  };

  // --- Analyze JD only (no resume needed) ---
  const handleAnalyzeJd = async (appId: string) => {
    const appData = applications.find(a => a.id === appId);
    if (!appData?.description || appData.description.length < 50) {
      alert('No job description available (minimum 50 characters).');
      return;
    }

    setApplications(apps => apps.map(app =>
      app.id === appId ? { ...app, matchScore: -1 } : app
    ));

    try {
      const jdResult = await analyzeJobDescription(appId);
      setApplications(apps => apps.map(app =>
        app.id === appId ? {
          ...app,
          matchScore: app.matchScore === -1 ? undefined : app.matchScore,
          jdSkills: jdResult.skills,
          jdKeywords: jdResult.keywords,
          jdSuggestedPhrases: jdResult.suggestedPhrases,
          jdRoleRequirements: jdResult.roleRequirements,
        } : app
      ));
    } catch (e: any) {
      console.error('JD analysis failed:', e);
      setApplications(apps => apps.map(app =>
        app.id === appId ? { ...app, matchScore: undefined } : app
      ));
      alert(`JD analysis failed: ${e.message || 'Unknown error'}`);
    }
  };

  // --- Analyze Resume (process + match) ---
  const handleAnalyzeResume = async (appId: string) => {
    const appData = applications.find(a => a.id === appId);

    // If no resume, fall back to JD-only analysis
    if (!appData?.matchedResumeId || !appData?.resumePath) {
      return handleAnalyzeJd(appId);
    }

    // Show analyzing state
    setApplications(apps => apps.map(app =>
      app.id === appId ? { ...app, matchScore: -1 } : app // -1 = analyzing indicator
    ));

    try {
      // Process resume (extract skills + embedding)
      const processResult = await processResume(appData.matchedResumeId, appData.resumePath);

      // Run match analysis if skills extracted and JD exists
      if (processResult.skills.length > 0 && appData.description && appData.description.length >= 50) {
        const matchResult = await matchResumeToApplication(appId, appData.matchedResumeId);
        const db = getFirestore();
        const refDoc = doc(db, 'applications', appId);
        await updateDoc(refDoc, {
          matchScore: matchResult.matchScore,
          skillMatchScore: matchResult.skillMatchScore,
          embeddingScore: matchResult.embeddingScore,
          updatedAt: serverTimestamp(),
        });
        setApplications(apps => apps.map(app =>
          app.id === appId ? {
            ...app,
            matchScore: matchResult.matchScore,
            skillMatchScore: matchResult.skillMatchScore,
            embeddingScore: matchResult.embeddingScore,
            matchedSkills: matchResult.matchedSkills,
            missingSkills: matchResult.missingSkills,
            matchedKeywords: matchResult.matchedKeywords,
            missingKeywords: matchResult.missingKeywords,
            jdSkills: matchResult.jdSkills,
            jdKeywords: matchResult.jdKeywords,
            matchedResumeId: appData.matchedResumeId,
          } : app
        ));
      } else {
        setApplications(apps => apps.map(app =>
          app.id === appId ? { ...app, matchScore: undefined } : app
        ));
        if (!appData.description || appData.description.length < 50) {
          alert('Resume processed but no job description available for matching.');
        } else {
          alert('Resume processed but no skills could be extracted.');
        }
      }
    } catch (e: any) {
      console.error('Analysis failed:', e);
      setApplications(apps => apps.map(app =>
        app.id === appId ? { ...app, matchScore: undefined } : app
      ));
      alert(`Analysis failed: ${e.message || 'Unknown error'}`);
    }
  };

  // --- Remove Resume/Cover Letter ---
  const handleFileRemove = async (appId: string, fileType: 'resume' | 'coverLetter') => {
    try {
      const db = getFirestore();
      const refDoc = doc(db, 'applications', appId);
      const updateData = fileType === 'resume'
        ? { resumeUrl: null, resumePath: null, updatedAt: serverTimestamp() }
        : { coverLetterUrl: null, coverLetterPath: null, updatedAt: serverTimestamp() };

      await updateDoc(refDoc, updateData);

      // Update local state
      setApplications(apps => apps.map(app => {
        if (app.id === appId) {
          return fileType === 'resume'
            ? { ...app, resumeUrl: undefined, resumePath: undefined }
            : { ...app, coverLetterUrl: undefined, coverLetterPath: undefined };
        }
        return app;
      }));
    } catch (e) {
      console.error('Failed to remove file', e);
      alert('Failed to remove file. Please try again.');
    }
  };

  // --- Filtering ---
  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      if (app.archived) return false;

      // Text filter
      const text = searchText.toLowerCase();
      const matchesText =
        !text ||
        app.jobTitle?.toLowerCase().includes(text) ||
        app.company?.toLowerCase().includes(text) ||
        app.location?.toLowerCase().includes(text) ||
        app.notes?.toLowerCase().includes(text);

      // Date filter
      const appliedDate = app.appliedDate instanceof Date
        ? app.appliedDate
        : (app.appliedDate as any)?.toDate?.() || null;
      const matchesDate =
        (!startDate || (appliedDate && appliedDate >= startDate)) &&
        (!endDate || (appliedDate && appliedDate <= endDate));

      // Selected date filter (from contribution graph)
      let matchesSelectedDate = true;
      if (selectedDate && appliedDate) {
        // Compare date strings in local timezone
        const year = appliedDate.getFullYear();
        const month = String(appliedDate.getMonth() + 1).padStart(2, '0');
        const day = String(appliedDate.getDate()).padStart(2, '0');
        const appDateStr = `${year}-${month}-${day}`;
        matchesSelectedDate = appDateStr === selectedDate;
      } else if (selectedDate) {
        matchesSelectedDate = false;
      }

      return matchesText && matchesDate && matchesSelectedDate;
    });
  }, [applications, searchText, startDate, endDate, selectedDate]);

  // --- Group by status ---
  const grouped = useMemo(() => {
    return COLUMNS.reduce((acc, status) => {
      acc[status] = filteredApplications.filter(a => a.status === status);
      return acc;
    }, {} as Record<ApplicationStatus, JobApplication[]>);
  }, [filteredApplications]);

  // --- Drag & Drop ---
  const [activeApp, setActiveApp] = useState<JobApplication | null>(null);
  // --- Drag & Drop ---
  const handleDragStart = (event: any) => {
  const appId = event.active.id.split('-')[1]; // extract app ID
  const app = applications.find(a => a.id === appId);
  if (app) setActiveApp(app);
};

const handleDragEnd = (event: any) => {
  setActiveApp(null);
  if (!event.over) return;

  const appId = event.active.id.split('-')[1]; // dragged app
  const newStatus = event.over.id as ApplicationStatus; // column ID

  if (!COLUMNS.includes(newStatus)) return;

  const app = applications.find(a => a.id === appId);
  if (!app || app.status === newStatus) return;

  handleStatusChange(app.id!, newStatus);
};



  if (loading) return <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center text-gray-600 dark:text-gray-400">Loading…</div>;

  return (
    <>
      {/* --- Selected Date Indicator --- */}
      {selectedDate && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span className="text-xs sm:text-sm text-blue-800 dark:text-blue-300">
            📅 Showing <strong>{filteredApplications.length}</strong> application{filteredApplications.length !== 1 ? 's' : ''} from: <strong>{new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
          </span>
          <button
            onClick={onClearDateFilter}
            className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 self-start sm:self-auto"
          >
            Clear Filter
          </button>
        </div>
      )}

      {/* --- Global Search & Date Range --- */}
      <div className="p-3 sm:p-4 bg-gray-100 dark:bg-gray-800 rounded mb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
        <input
          type="text"
          placeholder="Search Role, Company, Location..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded text-sm sm:text-base sm:col-span-2 md:col-span-1"
        />
        <input
          type="date"
          value={startDate ? startDate.toISOString().slice(0, 10) : ''}
          onChange={e => setStartDate(e.target.value ? new Date(e.target.value) : null)}
          className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded text-sm sm:text-base"
        />
        <input
          type="date"
          value={endDate ? endDate.toISOString().slice(0, 10) : ''}
          onChange={e => setEndDate(e.target.value ? new Date(e.target.value) : null)}
          className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded text-sm sm:text-base"
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Applications</h3>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-3 py-2 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-lg text-sm sm:text-base w-full sm:w-auto"
          >
            + Add Application
          </button>
        </div>

        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          {activeApp && (
            <DragOverlay>
              <KanbanCard
                id={`${activeApp.status}-${activeApp.id}`}
                app={activeApp}
                onDownload={handleDownload}
                onArchive={() => setArchiveModal({ open: true, appId: activeApp.id })}
                onFileUpload={handleFileUpload}
                onFileRemove={handleFileRemove}
                onAnalyze={() => {}}
                onSelect={() => {}}
              />
            </DragOverlay>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 p-4 sm:p-6">
            {COLUMNS.map(status => (
              <KanbanColumn
                key={status}
                status={status}
                apps={grouped[status]}
                onDownload={handleDownload}
                onArchive={appId => setArchiveModal({ open: true, appId })}
                onFileUpload={handleFileUpload}
                onFileRemove={handleFileRemove}
                onAnalyze={handleAnalyzeResume}
                onSelect={(app) => setSelectedAppId(app.id || null)}
              />
            ))}
          </div>
        </DndContext>
      </div>

      {showAddModal && (
        <AddApplicationModal userId={userId} onClose={() => setShowAddModal(false)} onCreated={loadApplications} />
      )}

      {/* Archive Modal */}
      {archiveModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded shadow w-full max-w-sm">
            <p className="mb-4 text-sm sm:text-base text-gray-900 dark:text-white">Are you sure you want to archive this application?</p>
            <div className="flex justify-end gap-2">
              <button className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded text-sm" onClick={() => setArchiveModal({ open: false })}>Cancel</button>
              <button className="px-3 py-1.5 bg-red-600 text-white rounded text-sm" onClick={handleArchive}>Archive</button>
            </div>
          </div>
        </div>
      )}

      {/* Application Detail Modal */}
      {selectedApp && (
        <ApplicationDetailModal
          app={selectedApp}
          onClose={() => setSelectedAppId(null)}
          onSaveJd={handleSaveJd}
          onAnalyze={handleAnalyzeJd}
          onFullDetails={(id) => navigate(`/applications/${id}`)}
        />
      )}
    </>
  );
}

// --- Kanban Column ---
interface KanbanColumnProps {
  status: ApplicationStatus;
  apps: JobApplication[];
  onDownload: (path?: string) => void;
  onArchive: (appId: string) => void;
  onFileUpload: (appId: string, file: File, fileType: 'resume' | 'coverLetter') => void;
  onFileRemove: (appId: string, fileType: 'resume' | 'coverLetter') => void;
  onAnalyze: (appId: string) => void;
  onSelect: (app: JobApplication) => void;
}
function KanbanColumn({ status, apps, onDownload, onArchive, onFileUpload, onFileRemove, onAnalyze, onSelect }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg p-3 sm:p-4 min-h-[120px] sm:min-h-[200px] transition ${isOver ? 'bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-300 dark:ring-blue-700' : 'bg-gray-50 dark:bg-gray-900'}`}
    >
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">{STATUS_LABELS[status]}</h3>
        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">{apps.length}</span>
      </div>

      <SortableContext
        items={apps.map(a => `${status}-${a.id}`)}
        strategy={verticalListSortingStrategy}
      >
        {apps.map(app => (
          <KanbanCard
            key={`${status}-${app.id}`}
            id={`${status}-${app.id}`}
            app={app}
            onDownload={onDownload}
            onArchive={() => onArchive(app.id!)}
            onFileUpload={onFileUpload}
            onFileRemove={onFileRemove}
            onAnalyze={() => onAnalyze(app.id!)}
            onSelect={() => onSelect(app)}
          />
        ))}
      </SortableContext>
    </div>
  );
}

interface KanbanCardProps {
  id: string;
  app: JobApplication;
  onDownload: (path?: string) => void;
  onArchive: () => void;
  onFileUpload: (appId: string, file: File, fileType: 'resume' | 'coverLetter') => void;
  onFileRemove: (appId: string, fileType: 'resume' | 'coverLetter') => void;
  onAnalyze: () => void;
  onSelect: () => void;
}

// Helper to get match score color classes
function getMatchScoreColor(score: number): string {
  if (score >= 70) return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
  if (score >= 50) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
  return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
}

function KanbanCard({ id, app, onDownload, onArchive, onFileUpload, onFileRemove, onAnalyze, onSelect }: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const [uploading, setUploading] = useState<'resume' | 'coverLetter' | null>(null);
  const [showResumeMenu, setShowResumeMenu] = useState(false);
  const [showCoverMenu, setShowCoverMenu] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, fileType: 'resume' | 'coverLetter') => {
    const file = e.target.files?.[0];
    if (!file || !app.id) return;
    setUploading(fileType);
    try {
      await onFileUpload(app.id, file, fileType);
    } finally {
      setUploading(null);
      setShowResumeMenu(false);
      setShowCoverMenu(false);
    }
    e.target.value = ''; // Reset input
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't open modal if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('a, button, label, input, select')) return;
    onSelect();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleCardClick}
      className="bg-white dark:bg-gray-800 p-2.5 sm:p-3 rounded-lg shadow border border-gray-200 dark:border-gray-700 cursor-grab active:cursor-grabbing mb-2 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
    >
      <div className="flex justify-between items-start gap-2">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <h5 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white line-clamp-2">{app.jobTitle}</h5>
          {/* Match Score Badge */}
          {typeof app.matchScore === 'number' && app.matchScore === -1 && (
            <span className="text-[9px] sm:text-[10px] px-1 py-0.5 rounded font-medium flex-shrink-0 bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 animate-pulse">
              Analyzing...
            </span>
          )}
          {typeof app.matchScore === 'number' && app.matchScore >= 0 && (
            <span
              className={`text-[9px] sm:text-[10px] px-1 py-0.5 rounded font-medium flex-shrink-0 ${getMatchScoreColor(app.matchScore)}`}
              title={`Match: ${app.matchScore}% (Skill: ${app.skillMatchScore || 0}%, Semantic: ${app.embeddingScore || 0}%)`}
            >
              {app.matchScore}%
            </span>
          )}
        </div>
        <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded whitespace-nowrap ${STATUS_COLORS[app.status]}`}>
          {STATUS_LABELS[app.status]}
        </span>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{app.company}</p>
      {app.location && <p className="text-xs text-gray-500 dark:text-gray-500 truncate">{app.location}</p>}
      {/* Skill & Semantic score breakdown */}
      {typeof app.matchScore === 'number' && (typeof app.skillMatchScore === 'number' || typeof app.embeddingScore === 'number') && (
        <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500 dark:text-gray-400">
          {typeof app.skillMatchScore === 'number' && (
            <span>Skill: <span className={app.skillMatchScore >= 70 ? 'text-green-600 dark:text-green-400' : app.skillMatchScore >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}>{app.skillMatchScore}%</span></span>
          )}
          {typeof app.embeddingScore === 'number' && (
            <span>Semantic: <span className={app.embeddingScore >= 70 ? 'text-green-600 dark:text-green-400' : app.embeddingScore >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}>{app.embeddingScore}%</span></span>
          )}
        </div>
      )}
      <p className="text-[10px] sm:text-xs text-gray-400">
        {app.appliedDate instanceof Date
          ? app.appliedDate.toLocaleDateString()
          : (app.appliedDate as any)?.toDate?.()?.toLocaleDateString() || 'N/A'}
      </p>
      <div className="flex gap-2 mt-2 flex-wrap">
        {app.jobUrl && (
          <a
            href={app.jobUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-[10px] sm:text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Job Post
          </a>
        )}
        {/* Resume with dropdown menu */}
        {app.resumePath ? (
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowResumeMenu(!showResumeMenu); setShowCoverMenu(false); }}
              className="text-[10px] sm:text-xs text-green-700 dark:text-green-400 hover:underline flex items-center gap-0.5"
            >
              Resume
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {showResumeMenu && (
              <div
                className="absolute left-0 top-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-lg z-10 min-w-[80px]"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => { onDownload(app.resumePath); setShowResumeMenu(false); }}
                  className="block w-full text-left px-3 py-1.5 text-[10px] sm:text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  View
                </button>
                <label className="block w-full text-left px-3 py-1.5 text-[10px] sm:text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                  {uploading === 'resume' ? 'Uploading...' : 'Replace'}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'resume')}
                    disabled={uploading !== null}
                  />
                </label>
                {app.description && app.description.length >= 50 && (
                  <button
                    onClick={() => { onAnalyze(); setShowResumeMenu(false); }}
                    className="block w-full text-left px-3 py-1.5 text-[10px] sm:text-xs text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    {app.matchScore === -1 ? 'Analyzing...' : 'Analyze'}
                  </button>
                )}
                <button
                  onClick={() => { onFileRemove(app.id!, 'resume'); setShowResumeMenu(false); }}
                  className="block w-full text-left px-3 py-1.5 text-[10px] sm:text-xs text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        ) : (
          <label className="text-[10px] sm:text-xs text-green-600 dark:text-green-500 hover:underline cursor-pointer">
            {uploading === 'resume' ? 'Uploading...' : '+ Resume'}
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => handleFileChange(e, 'resume')}
              onClick={(e) => e.stopPropagation()}
              disabled={uploading !== null}
            />
          </label>
        )}
        {/* Cover Letter with dropdown menu */}
        {app.coverLetterPath ? (
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowCoverMenu(!showCoverMenu); setShowResumeMenu(false); }}
              className="text-[10px] sm:text-xs text-purple-700 dark:text-purple-400 hover:underline flex items-center gap-0.5"
            >
              Cover
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {showCoverMenu && (
              <div
                className="absolute left-0 top-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-lg z-10 min-w-[80px]"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => { onDownload(app.coverLetterPath); setShowCoverMenu(false); }}
                  className="block w-full text-left px-3 py-1.5 text-[10px] sm:text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  View
                </button>
                <label className="block w-full text-left px-3 py-1.5 text-[10px] sm:text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                  {uploading === 'coverLetter' ? 'Uploading...' : 'Replace'}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'coverLetter')}
                    disabled={uploading !== null}
                  />
                </label>
                <button
                  onClick={() => { onFileRemove(app.id!, 'coverLetter'); setShowCoverMenu(false); }}
                  className="block w-full text-left px-3 py-1.5 text-[10px] sm:text-xs text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        ) : (
          <label className="text-[10px] sm:text-xs text-purple-600 dark:text-purple-500 hover:underline cursor-pointer">
            {uploading === 'coverLetter' ? 'Uploading...' : '+ Cover'}
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => handleFileChange(e, 'coverLetter')}
              onClick={(e) => e.stopPropagation()}
              disabled={uploading !== null}
            />
          </label>
        )}
        <button onClick={onArchive} className="text-[10px] sm:text-xs text-red-700 dark:text-red-400 hover:underline">Archive</button>
      </div>
    </div>
  );
}
